import { HelpTopic } from './helpContent';

export const zonesHelpTopics: HelpTopic[] = [
  {
    id: 'zones-framework',
    label: 'FZI / RQI',
    title: 'FZI and RQI Framework',
    intro:
      'FZI and RQI are petrophysical indices that combine permeability and porosity into a single rock-quality discriminator. They form the foundation of Hydraulic Flow Unit classification and drive all zone-based calculations in this application.',
    sections: [
      {
        id: 'rqi',
        title: 'RQI — Reservoir Quality Index',
        blocks: [
          {
            type: 'paragraph',
            text: 'RQI is a single number that captures how easily fluids can flow through the rock. It scales as the square root of the permeability-to-porosity ratio, making it sensitive to both storage capacity and conductivity.',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'RQI = 0.0314 × √(k / φ)',
                description: 'k = permeability (mD), φ = porosity (fraction). The constant 0.0314 converts to standard μm units.',
                effect: 'Higher RQI means better flow quality. Doubling permeability at constant porosity increases RQI by ~41%; doubling porosity at constant permeability decreases RQI by ~29%.',
              },
              {
                name: 'PHIZ = φ / (1 − φ)',
                description: 'Normalized Porosity Index. Rescales porosity to remove the 0–1 constraint.',
                effect: 'Emphasizes differences at both low and high porosity ends. Used as the x-axis on the RQI vs. PHIZ log-log plot.',
              },
            ],
          },
        ],
      },
      {
        id: 'fzi',
        title: 'FZI — Flow Zone Indicator',
        blocks: [
          {
            type: 'paragraph',
            text: 'FZI is derived from RQI and PHIZ. Samples with the same FZI plot on the same unit-slope line in the log(RQI) vs. log(PHIZ) space, confirming they belong to the same Hydraulic Flow Unit.',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'FZI = RQI / PHIZ',
                description: 'Equivalent to RQI × (1 − φ) / φ.',
                effect: 'The primary x-axis variable for all correlations. Samples with similar FZI have similar pore geometry and flow behavior regardless of depth or well.',
              },
            ],
          },
          {
            type: 'bullets',
            items: [
              'FZI is preferred when samples span a wide porosity range — it decouples lithology effects from pore-throat quality.',
              'RQI is an alternative x-axis when porosity variation is small or when comparing with external datasets.',
              'The toggle between FZI and RQI on the Correlation page changes only the x-axis of the regression — zone mean values are re-interpreted accordingly.',
            ],
          },
        ],
      },
      {
        id: 'hfu-concept',
        title: 'Hydraulic Flow Units (HFU)',
        blocks: [
          {
            type: 'paragraph',
            text: 'A Hydraulic Flow Unit is a mappable interval of rock with consistent pore geometry and flow behavior. Samples in the same HFU fall on the same unit-slope line in the log(RQI) vs. log(PHIZ) plot.',
          },
          {
            type: 'bullets',
            items: [
              'HFU boundaries are set by defining FZI cutoff values in the Flow Unit Bands tab.',
              'Each HFU is assigned a label (HFU 1, HFU 2, …) from lowest to highest FZI — higher numbers mean better reservoir quality.',
              'HFU classification is the link between core measurements and reservoir model zonation.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'zones-correlation',
    label: 'Correlation',
    title: 'Correlation Analysis',
    intro:
      'Correlation analysis extracts petrophysical properties from completed sample analyses and fits regression functions against FZI or RQI. These functions are then used to predict properties for any zone mean value.',
    sections: [
      {
        id: 'what-correlation-does',
        title: 'What correlation analysis does',
        blocks: [
          {
            type: 'paragraph',
            text: 'For each sample with a completed Capillary Pressure or Kr analysis, the system reads the analysis outputs (best_a, best_b, lambda_variable, Pc_max, Swir, Sor, Kro, Krw, No, Nw, etc.) and pairs them against the sample\'s FZI or RQI value.',
          },
          {
            type: 'bullets',
            items: [
              'The result is a regression function per property — one equation per output that maps FZI → property value.',
              'These functions drive the zone-value calculations.',
              'Re-running correlation after adding or removing samples updates all functions and immediately re-populates all existing zone values.',
            ],
          },
          {
            type: 'note',
            title: 'Zones without correlation',
            text: 'If correlation has not been run before creating zones, zone_values will be empty. Always run correlation on the Correlation page first, with a representative set of samples, before creating zones.',
          },
        ],
      },
      {
        id: 'regression-models',
        title: 'Regression models',
        blocks: [
          {
            type: 'paragraph',
            text: 'Four model types are evaluated for each property. The one with the highest R² is automatically selected.',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Linear: y = c × x + b',
                description: 'Straight-line relationship between FZI and the property.',
                effect: 'Best when the property increases or decreases proportionally with FZI.',
              },
              {
                name: 'Power: y = c × x^b',
                description: 'Straight line on a log-log plot.',
                effect: 'Best for properties that scale multiplicatively with rock quality (common for permeability-related outputs).',
              },
              {
                name: 'Logarithmic: y = c + b × ln(x)',
                description: 'Diminishing returns as FZI increases.',
                effect: 'Best when the property grows quickly at low FZI and levels off at high FZI.',
              },
              {
                name: 'Exponential: y = c × e^(b × x)',
                description: 'Accelerating growth or decay with FZI.',
                effect: 'Best when the property changes by orders of magnitude across the FZI range.',
              },
            ],
          },
        ],
      },
      {
        id: 'supported-strategies',
        title: 'Supported analysis strategies',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Liao — capillary pressure parameters: best_a, best_b, lambda_variable, Pc_max, Swir, Pce.',
              'Brooks-Corey — capillary pressure parameters: Pe, fitted exponent m, Pc_max and Swir. The classic pore-size distribution index is λBC = −1/m.',
              'Water-Oil Kr — relative permeability parameters: Sor_w, Kro endpoint, Krw endpoint, No, Nw.',
              'Gas-Oil Kr — gas relative permeability parameters.',
              'Each strategy produces its own set of regression functions for the zone calculations.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'zones-understanding',
    label: 'Zones',
    title: 'Understanding Zones',
    intro:
      'A zone is a depth interval with a representative FZI value (the mean). After correlation, the system uses each zone\'s mean FZI as the input to the regression equations and stores the predicted petrophysical properties as zone_values.',
    sections: [
      {
        id: 'what-a-zone-is',
        title: 'What a zone is',
        blocks: [
          {
            type: 'bullets',
            items: [
              'A depth interval defined by a start value, an end value, and a mean value.',
              'Each zone represents a rock interval with a characteristic FZI — the mean.',
              'Zones are project-level: shared across all wells and samples in the project.',
              'Each zone has a color used consistently across all six charts.',
            ],
          },
        ],
      },
      {
        id: 'zone-values',
        title: 'Zone values — how properties are assigned',
        blocks: [
          {
            type: 'paragraph',
            text: 'Once correlation is complete, every zone receives a set of predicted petrophysical properties calculated from its mean FZI:',
          },
          {
            type: 'bullets',
            items: [
              'zone_values.liao_values — best_a, best_b, lambda_variable, Pc_max, Swir, Pce, Sor_w, Kro, Krw, No, Nw (from Liao regression functions).',
              'zone_values.lambda_values — pe, lambda_value, Pc_max, Swir (from Lambda/Brooks-Corey regression functions).',
              'These values are used downstream as inputs for system saturation calculations and reservoir model exports.',
            ],
          },
          {
            type: 'note',
            title: 'Automatic recalculation',
            text: 'Zone values are recalculated automatically whenever the zone geometry (start, end, mean) changes or when correlation is re-run with new samples. No manual refresh is required.',
          },
        ],
      },
      {
        id: 'zone-mean',
        title: 'Why the zone mean matters',
        blocks: [
          {
            type: 'paragraph',
            text: 'The mean FZI is the single x-value used in every regression equation for that zone. All zone_values are computed from it.',
          },
          {
            type: 'bullets',
            items: [
              'Default: mean is auto-calculated as (start + end) / 2.',
              'Override the mean manually when the FZI distribution within the interval is skewed — for example, if most samples cluster near the high end of the zone.',
              'A practical check: compare the auto-calculated mean against the Median chart. If they differ significantly, the distribution is skewed and the mean should be set to the median value.',
              'Changing start or end re-triggers zone_values recalculation automatically.',
            ],
          },
        ],
      },
      {
        id: 'zone-lifecycle',
        title: 'Zone lifecycle',
        blocks: [
          {
            type: 'bullets',
            items: [
              '1. Zone created (start, end, mean, color) → zone_values populated from fzi_functions.',
              '2. Zone geometry edited and saved → zone_values recalculated with updated mean.',
              '3. Zone deleted → removed from all charts and from the project zone list.',
              '4. Correlation re-run with new samples → all zone_values recalculated with updated regression functions.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'zones-management',
    label: 'Managing Zones',
    title: 'Creating and Managing Zones',
    intro:
      'Zones are created and edited through Flow Unit Settings. Changes take effect immediately and trigger zone value recalculation in the background.',
    sections: [
      {
        id: 'adding-zones',
        title: 'Adding zones',
        blocks: [
          {
            type: 'paragraph',
            text: 'Open Flow Unit Settings and go to the Zone Management tab. Each row is one zone.',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Min (start)',
                description: 'Lower boundary of the depth interval.',
                effect: 'Defines the start of the zone. Along with Max, it controls the auto-calculated mean.',
              },
              {
                name: 'Max (end)',
                description: 'Upper boundary of the depth interval.',
                effect: 'Defines the end of the zone.',
              },
              {
                name: 'Mean',
                description: 'Representative FZI value for the zone. Auto-filled as (Min + Max) / 2 if left empty.',
                effect: 'The single x-value used in all regression equations for this zone. Override when the FZI distribution is asymmetric.',
              },
              {
                name: 'Color',
                description: 'Hex color code assigned to this zone.',
                effect: 'Used consistently across all six charts for visual identification.',
              },
            ],
          },
          {
            type: 'note',
            title: 'Bulk entry from spreadsheet',
            text: 'Paste tab-separated values directly from a spreadsheet into the first Min field. Column order: Min, Mean, Max, Color. The system parses the values and fills in all rows automatically — the fastest way to enter many zones at once.',
          },
        ],
      },
      {
        id: 'editing-zones',
        title: 'Editing zones',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Existing zones are listed below the input form in the Zone Management tab.',
              'Edit any field directly — a Save button appears when values differ from the last saved state.',
              'Saving triggers zone_values recalculation for that zone only.',
              'Color changes do not trigger recalculation — only start, end, and mean do.',
            ],
          },
        ],
      },
      {
        id: 'deleting-zones',
        title: 'Deleting zones',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Check one or more zones to enable the Remove Selected button.',
              'A confirmation dialog appears before deletion — this action cannot be undone.',
              'Deleted zones are removed from all charts immediately.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'zones-hfu-bands',
    label: 'HFU Bands',
    title: 'HFU Bands — Flow Unit Classification',
    intro:
      'HFU bands divide the RQI vs. PHIZ log-log plot into Hydraulic Flow Units by placing horizontal cutoff lines at natural gaps in the data distribution.',
    sections: [
      {
        id: 'what-hfu-bands-are',
        title: 'What HFU bands are',
        blocks: [
          {
            type: 'paragraph',
            text: 'Each band is defined by an upper cutoff value and a display color. The system assigns a label (HFU 1, HFU 2, …) automatically from lowest to highest FZI. The last band always extends to infinity.',
          },
          {
            type: 'bullets',
            items: [
              'Higher-numbered HFUs (higher FZI) represent better reservoir quality.',
              'Samples within the same band share similar pore geometry and are expected to have similar flow behavior.',
              'Bands are overlaid on the RQI vs. PHIZ chart as horizontal reference lines.',
              'The Distribution, Mean, and Median charts use HFU band colors to group samples.',
            ],
          },
        ],
      },
      {
        id: 'setting-cutoffs',
        title: 'How to set band cutoffs',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Open Flow Unit Settings → Flow Unit Bands tab.',
              'Enter the upper FZI/RQI boundary for each band and choose a color.',
              'Look for natural gaps or sparse regions in the RQI vs. PHIZ log-log chart — those are the natural HFU boundaries.',
              'The last band requires no cutoff; it is enforced as infinity automatically.',
              'Start with 3–5 bands and refine based on how well samples cluster within each band on the log-log plot.',
            ],
          },
          {
            type: 'note',
            title: 'Reading the log-log plot for band placement',
            text: 'On the log(RQI) vs. log(PHIZ) chart, parallel unit-slope lines represent constant FZI values. Place band cutoffs at FZI values that fall in the gaps between groups of co-linear sample points. Each group that forms its own line should ideally be its own HFU.',
          },
        ],
      },
    ],
  },
  {
    id: 'zones-lambda',
    label: 'Lambda Variable',
    title: 'Lambda Variable — Stratification Indicator',
    intro:
      'The lambda variable is a dimensionless number derived from the Liao model parameters. It measures pore size distribution uniformity and is the key output for ranking intervals by flow heterogeneity.',
    sections: [
      {
        id: 'lambda-definition',
        title: 'What lambda variable is',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'λ = log₁₀(1 + b) / log₁₀((1 + a) / (Pce + a))',
                description: 'Derived from Liao parameters: a (curve coefficient), b (saturation exponent), Pce (entry pressure normalized).',
                effect: 'Higher λ → more uniform pore sizes → better-sorted, more predictable rock. Lower λ → wider pore size distribution → more heterogeneous, potentially baffled flow.',
              },
            ],
          },
          {
            type: 'bullets',
            items: [
              'Per-sample: calculated from the individual sample\'s Liao fit on the Capillary Pressure page.',
              'Per-zone: calculated using the regression-predicted best_a, best_b, and Pce for that zone\'s mean FZI.',
              'The Stratification chart displays the lambda variable per zone as a bar chart.',
            ],
          },
        ],
      },
      {
        id: 'lambda-interpretation',
        title: 'How to interpret lambda in zones',
        blocks: [
          {
            type: 'bullets',
            items: [
              'High lambda → well-sorted rock with uniform pore throats → predictable, high-efficiency displacement.',
              'Low lambda → heterogeneous pore network → risk of early water breakthrough and poor sweep efficiency.',
              'Use the lambda bar chart alongside the Stratification Lorenz chart to rank intervals: high lambda + steep Lorenz segment = priority injection interval.',
              'Zones where lambda is very low may benefit from EOR or mechanical diversion to improve sweep.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'zones-charts',
    label: 'Charts',
    title: 'Chart Interpretation',
    intro:
      'The Zones tab shows six charts that together provide a complete picture of reservoir quality, HFU classification, flow heterogeneity, and zone coherence. Each chart targets a specific diagnostic question.',
    sections: [
      {
        id: 'chart-rqi-phiz',
        title: 'RQI vs. PHIZ (Log-Log)',
        blocks: [
          {
            type: 'paragraph',
            text: 'The primary HFU classification chart. Each point is one sample, plotted on a log-log scale.',
          },
          {
            type: 'bullets',
            items: [
              'Unit-slope lines: each parallel diagonal represents a constant FZI value. Samples on the same line belong to the same HFU.',
              'HFU band lines: horizontal references showing the band cutoff boundaries.',
              'Zone coloring: each sample is colored by the zone it falls within.',
              'Clustering along distinct unit-slope lines confirms clear HFU separation.',
              'Scattered, unclustered points suggest high heterogeneity or misplaced zone boundaries.',
              'Outliers well above or below a zone\'s trend line may indicate fractures, vugs, or clay-damaged samples.',
            ],
          },
        ],
      },
      {
        id: 'chart-perm-poro',
        title: 'Permeability vs. Porosity (Log-Log)',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Each sample plotted as permeability (k) vs. porosity (φ) on a log-log scale.',
              'Samples are colored by zone; a power-law trend line is fitted per zone.',
              'Tight clusters with steep trends (high slope) indicate good, well-defined rock types.',
              'Flat or scattered trends indicate poor rock quality or flow unit mixing within a zone.',
              'Points above the zone trend may indicate natural fractures or vugs; points below may indicate clay plugging or cementation.',
            ],
          },
        ],
      },
      {
        id: 'chart-lorenz',
        title: 'Stratigraphic Lorenz Chart',
        blocks: [
          {
            type: 'paragraph',
            text: 'Plots cumulative normalized flow capacity (k × h) against cumulative normalized storage capacity (φ × h), ordered by stratigraphic depth from top to bottom.',
          },
          {
            type: 'bullets',
            items: [
              'The diagonal (45° line) represents a perfectly homogeneous reservoir — equal flow and storage capacity at every depth.',
              'Lorenz coefficient: the area between the curve and the diagonal, ranging from 0 (homogeneous) to 1 (completely heterogeneous).',
              'Steep segments = high-flow, low-storage intervals — the best flow units, corresponding to high-FZI zones.',
              'Flat segments = low-flow, high-storage intervals — tight rock with poor conductivity.',
              'Abrupt slope changes (deflection points) mark flow unit boundaries and should ideally coincide with zone boundaries.',
            ],
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Core Sample Length (h)',
                description: 'The physical length of each core plug in centimeters. Used as the thickness weight h in the φ×h (storage) and k×h (flow) integrals.',
                effect: 'Samples with a longer core contribute proportionally more to the cumulative flow and storage capacity. When Core Sample Length is not provided, the calculation falls back to a unit weight of 1 for that sample — every sample without a length is treated as if it has the same relative thickness. For the most accurate Lorenz curve, enter the actual core plug length for each sample.',
              },
            ],
          },
          {
            type: 'note',
            title: 'Using Lorenz for zone validation',
            text: 'Overlay zone boundaries on the Lorenz curve. If a major deflection point falls within a zone rather than at its boundary, the zone start or end depth needs adjustment. Well-placed boundaries capture each slope regime in its own zone.',
          },
        ],
      },
      {
        id: 'chart-distribution',
        title: 'Distribution Chart',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Shows the FZI (or RQI) value distribution for each zone, typically as a histogram or box plot.',
              'Each zone is colored by its assigned color.',
              'Narrow distribution → coherent rock type captured by the zone.',
              'Wide or bimodal distribution → the zone spans multiple HFUs and should be split.',
              'Overlap between adjacent zones → gradational boundary; consider adjusting zone start/end or the mean.',
            ],
          },
        ],
      },
      {
        id: 'chart-mean',
        title: 'Mean Chart',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Displays the mean FZI (or RQI) value per zone as a bar chart.',
              'Large differences between adjacent zone means confirm a clear flow unit boundary between them.',
              'Very similar means for neighboring zones may indicate the boundary is unnecessary or misplaced.',
              'The mean value shown here is the one used as the x-input for all zone_values regression equations.',
            ],
          },
        ],
      },
      {
        id: 'chart-median',
        title: 'Median Chart',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Displays the median FZI (or RQI) value per zone as a bar chart.',
              'The median is more robust to outliers than the mean.',
              'If median ≈ mean for a zone: the distribution is roughly symmetric and the auto-calculated mean is reliable.',
              'If median ≠ mean significantly: the distribution is skewed — consider manually setting the zone mean to the median value to improve zone_values accuracy.',
            ],
          },
          {
            type: 'note',
            title: 'Mean override workflow',
            text: 'Compare the Mean and Median charts side-by-side. For any zone where they differ by more than ~20%, open Flow Unit Settings, edit that zone, and set the Mean field to the median value shown here. Save to re-trigger zone_values recalculation.',
          },
        ],
      },
    ],
  },
  {
    id: 'zones-workflow',
    label: 'Workflow',
    title: 'End-to-End Workflow',
    intro:
      'Follow this sequence to build a reliable zone classification from raw sample analyses to chart-validated flow units.',
    sections: [
      {
        id: 'workflow-steps',
        title: 'Step-by-step guide',
        blocks: [
          {
            type: 'bullets',
            items: [
              '1. Run sample analyses — complete Liao or Brooks-Corey fits on the Capillary Pressure page; complete Corey fits on the Kr Analysis page for at least a representative subset of samples.',
              '2. Run correlation — go to the Correlation page, select samples for each strategy (Liao, Lambda, Water-Oil Kr), choose FZI or RQI as the x-axis, and click Calculate.',
              '3. Verify regression quality — review R² values for each property. Remove or add samples to improve fits before proceeding. A poor regression will produce unreliable zone_values.',
              '4. Open Flow Unit Settings — click the Flow Unit Settings button on the Zones page header.',
              '5. Create zones — in the Zone Management tab, enter depth ranges (start, end, mean, color). Use the bulk paste feature to enter many zones at once from a spreadsheet.',
              '6. Confirm zone_values are populated — after saving zones, the system automatically applies the regression equations. Verify by checking the Zones page charts.',
              '7. Set HFU bands — in the Flow Unit Bands tab, add cutoff values at natural gaps visible in the RQI vs. PHIZ log-log chart. Start with 3–5 bands.',
              '8. Interpret the six charts — use RQI vs. PHIZ to verify HFU clustering; use Perm vs. Porosity to validate k-φ trends; use the Lorenz chart to rank intervals and validate zone boundaries; use Distribution, Mean, and Median to assess zone coherence.',
              '9. Refine zones — adjust start/end/mean based on chart feedback. For skewed distributions, override mean with the median. Re-save to trigger recalculation.',
            ],
          },
          {
            type: 'note',
            title: 'When to re-run correlation',
            text: 'Re-run correlation any time new samples are analyzed or existing sample fits are updated. All zone_values will be recalculated automatically after re-running, so zone geometry does not need to be re-entered.',
          },
        ],
      },
    ],
  },
];
